/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import organizationTranslations from "./organizationTranslations";
import personTranslations from "./personTranslations";
import { AbstractCase } from "../../types/Entities/AbstractCase";

const abstractLitigationCaseTranslations = {
  instanceLevel: (product: AbstractCase) => {
    switch (product.instanceLevel) {
      case 1:
        return "Berufungsverfahren";
      case 2:
        return "Revisionsverfahren";
      case 0:
      default:
        return "";
    }
  },
  opposingPartyLegalEntity: {
    ...organizationTranslations,
  },
  opposingPartyPerson: {
    ...personTranslations,
  },
  statementOfClaimFiledDate: {
    label: "Klage erhalten am",
  },
  contractualPartnerHasLawyer: {
    label: "Vertragspartner anwaltlich vertreten",
    options: {
      true: {
        label: "Ja",
      },
      false: {
        label: "Nein",
      },
    },
  },
  opponentLawyer: {
    ...personTranslations,
  },
  opponentLawFirm: {
    ...organizationTranslations,
  },
  defenseProcessStatus: {
    label: "Stand der Forderung",
    options: {
      none: {
        label: "Ich habe bisher nichts vom Vertragspartner gehört, fürchte aber, dass dies noch passieren wird",
      },
      paymentOrder: {
        label: "Ich habe einen Mahnbescheid erhalten (gelber Brief per Post)",
      },
      enforcementOrder: {
        label: "Ich habe einen Vollstreckungsbescheid erhalten (gelber Brief per Post)",
      },
      debtCollection: {
        label: "Der Vertragspartner hat ein Inkasso-Unternehmen eingeschaltet, von dem ich zur Zahlung aufgefordert oder gemahnt wurde",
      },
      reminder: {
        label: "Ich habe eine Mahnung oder Zahlungsaufforderung (Rechnung) von dem Vertragspartner erhalten",
      },
      lawsuit: {
        label: "Ich habe eine Klage vom Gericht erhalten",
      },
    },
  },
  clientLegalRepresentative: {
    question: (caseData: AbstractCase) =>
      caseData.clientLegalRepresentativeAmount === "2" ? "Wie heißt die erste gesetzliche Vertretungsperson?" : "Wie heißt der gesetzliche Vertreter?",
    gender: {
      label: (caseData: AbstractCase) =>
        caseData.clientLegalRepresentativeAmount === 2 ? "Anrede erste gesetzliche Vertretung" : "Anrede gesetzliche Vertretung",
      options: {
        male: { label: "Herr" },
        female: { label: "Frau" },
      },
    },
    givenName: {
      label: (caseData: AbstractCase) =>
        caseData.clientLegalRepresentativeAmount === 2 ? "Vorname erste gesetzliche Vertretung" : "Vorname gesetzliche Vertretung",
    },
    familyName: {
      label: (caseData: AbstractCase) =>
        caseData.clientLegalRepresentativeAmount === 2 ? "Nachname erste gesetzliche Vertretung" : "Nachname gesetzliche Vertretung",
    },
    residenceAddress: {
      question: (caseData: AbstractCase) =>
        caseData.clientLegalRepresentativeAmount === 2 ? "Wo wohnt die erste gesetzliche Vertretungsperson?" : "Wo wohnt der gesetzliche Vertreter?",
      streetAddress: {
        label: (caseData: AbstractCase) =>
          caseData.clientLegalRepresentativeAmount === 2
            ? "Straße und Hausnummer erste gesetzliche Vertretung"
            : "Straße und Hausnummer gesetzliche Vertretung",
      },
      postalCode: {
        label: (caseData: AbstractCase) => (caseData.clientLegalRepresentativeAmount === 2 ? "PLZ erste gesetzliche Vertretung" : "PLZ gesetzliche Vertretung"),
      },
      addressLocality: {
        label: (caseData: AbstractCase) => (caseData.clientLegalRepresentativeAmount === 2 ? "Ort erste gesetzliche Vertretung" : "Ort gesetzliche Vertretung"),
      },
    },
  },
  secondClientLegalRepresentative: {
    question: "Wie heißt die zweite gesetzliche Vertretungsperson?",
    gender: {
      label: "Anrede zweite gesetzliche Vertretung",
      options: {
        male: { label: "Herr" },
        female: { label: "Frau" },
      },
    },
    givenName: {
      label: "Vorname zweite gesetzliche Vertretung",
    },
    familyName: {
      label: "Nachname zweite gesetzliche Vertretung",
    },
    residenceAddress: {
      question: "Wo wohnt die zweite gesetzliche Vertretungsperson?",
      streetAddress: {
        label: "Straße und Hausnummer zweite gesetzliche Vertretung",
      },
      postalCode: {
        label: "PLZ zweite gesetzliche Vertretung",
      },
      addressLocality: {
        label: "Ort zweite gesetzliche Vertretung",
      },
    },
  },
  clientLegalRepresentativeAmount: {
    question: "Wie viele gesetzliche Vertreter hat die minderjährige Person?",
    options: {
      1: {
        label: "Einen",
      },
      2: {
        label: "Zwei",
      },
    },
  },
  legalRepresentativeType: {
    question: (caseData: AbstractCase) =>
      caseData.clientLegalRepresentativeAmount === "2" ? "Sind Sie einer der gesetzlichen Vertreter?" : "Wer ist der gesetzliche Vertreter?",
    options: {
      me: {
        label: (caseData: AbstractCase) => (caseData.clientLegalRepresentativeAmount === "2" ? "Ja" : "Ich bin die gesetzliche Vertretung"),
      },
      other: {
        label: (caseData: AbstractCase) => (caseData.clientLegalRepresentativeAmount === "2" ? "Nein" : "Jemand anders ist die gesetzliche Vertretung"),
      },
    },
  },
  clientLegalRepresentativesAuthorizationType: {
    question: "Wie ist die gesetzliche Vertretung aufgeteilt?",
    label: "Aufteilung der gesetzlichen Vertretung",
    options: {
      both: {
        label: "Gesamtberechtigung der beiden Vertreter",
      },
      individually: {
        label: "Beide gesetzliche Vertreter sind einzelberechtigt",
      },
    },
  },
  clientHasAlreadyClaimed: {
    label: "Schriftliche Forderung",
    options: {
      yes: {
        label: "Ja",
      },
      no: {
        label: "Nein",
      },
      true: {
        label: "Ja",
      },
      false: {
        label: "Nein",
      },
    },
  },
  claimedDateClient: {
    label: "Gefordert am",
  },
  claimedAmountClient: {
    label: "Forderungsbetrag",
    question: {
      none: "Wie viel Geld fordert Ihr Vertragspartner? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
      paymentOrder:
        "Wie viel Geld fordert Ihr Vertragspartner? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
      enforcementOrder:
        "Wie viel Geld fordert Ihr Vertragspartner? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
      debtCollection:
        "Wie viel Geld fordert Ihr Vertragspartner (ohne Mahn-/Inkassogebühren)? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
      reminder:
        "Wie viel Geld fordert Ihr Vertragspartner (ohne Mahn-/Inkassogebühren)? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
      lawsuit:
        "Wie viel Geld fordert Ihr Vertragspartner? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
    },
  },
  policeInvolved: {
    question: "Wurde die Polizei hinzugezogen?",
    label: "Polizei hinzugezogen?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  policeReportReferenceNumber: {
    label: "Aktenzeichen oder Vorgangsnummer",
  },
  policeDepartmentName: {
    label: "Name der Polizeidienststelle",
  },
};

export default abstractLitigationCaseTranslations;
